/**
 * target-melody.ts — target melody format + safe parser (Phase 7 scoring, task #1).
 *
 * `TargetMelody` is the stable contract for what the user is asked to sing: an
 * ordered list of notes on the capture/playback timeline (startMs/durMs), each a
 * MIDI pitch. The scoring engine (task #2) maps raw `NativeF0Frame`s onto these
 * notes — the format only fixes the shape; real song content is injected later.
 *
 * `parseTargetMelody` is a pure, deterministic guard over `unknown` input (parsed
 * JSON, asset, or hand-authored demo): it validates structure/ranges, defends
 * against negatives and duplicates, and returns notes sorted on the timeline.
 * Invalid input throws a descriptive `Error` — same input, same result.
 */

/** MIDI note numbers span the standard 0..127 range (A4 = 69 = 440 Hz). */
export const MIN_MIDI = 0;
export const MAX_MIDI = 127;

export type TargetNote = {
  /** MIDI note number, integer in [MIN_MIDI, MAX_MIDI]. */
  midi: number;
  /** Onset on the capture/playback timeline, milliseconds ≥ 0. */
  startMs: number;
  /** Duration in milliseconds, > 0. */
  durMs: number;
  /** Optional lyric/syllable for the note. */
  lyric?: string;
};

export type TargetMelody = {
  id: string;
  title: string;
  /** Optional tempo hint; not required for scoring (timeline is in ms). */
  bpm?: number;
  /** Notes sorted by startMs (ties by midi); guaranteed non-empty after parse. */
  notes: TargetNote[];
};

const isObject = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isFiniteNumber = (v: unknown): v is number =>
  typeof v === 'number' && Number.isFinite(v);

const fail = (msg: string): never => {
  throw new Error(`parseTargetMelody: ${msg}`);
};

const parseNote = (raw: unknown, index: number): TargetNote => {
  if (!isObject(raw)) return fail(`notes[${index}] must be an object`);

  const { midi, startMs, durMs, lyric } = raw;

  if (!isFiniteNumber(midi) || !Number.isInteger(midi)) {
    return fail(`notes[${index}].midi must be an integer`);
  }
  if (midi < MIN_MIDI || midi > MAX_MIDI) {
    return fail(
      `notes[${index}].midi ${midi} out of range [${MIN_MIDI}, ${MAX_MIDI}]`,
    );
  }
  if (!isFiniteNumber(startMs) || startMs < 0) {
    return fail(`notes[${index}].startMs must be a number ≥ 0`);
  }
  if (!isFiniteNumber(durMs) || durMs <= 0) {
    return fail(`notes[${index}].durMs must be a number > 0`);
  }
  if (lyric !== undefined && typeof lyric !== 'string') {
    return fail(`notes[${index}].lyric must be a string when present`);
  }

  const note: TargetNote = { midi, startMs, durMs };
  if (lyric !== undefined) note.lyric = lyric;
  return note;
};

/** Sort by onset, then pitch — deterministic ordering for ties. */
const byTimeline = (a: TargetNote, b: TargetNote): number =>
  a.startMs - b.startMs || a.midi - b.midi;

/** Drop exact-duplicate notes (same midi + startMs + durMs) after sorting. */
const dedupe = (notes: TargetNote[]): TargetNote[] => {
  const out: TargetNote[] = [];
  for (const note of notes) {
    const prev = out[out.length - 1];
    const isDup =
      prev &&
      prev.midi === note.midi &&
      prev.startMs === note.startMs &&
      prev.durMs === note.durMs;
    if (!isDup) out.push(note);
  }
  return out;
};

/**
 * Validate `unknown` input into a `TargetMelody`. Pure/deterministic; throws on
 * any structural, range, negative, or empty-notes violation.
 */
export const parseTargetMelody = (input: unknown): TargetMelody => {
  if (!isObject(input)) return fail('input must be an object');

  const { id, title, bpm, notes } = input;

  if (typeof id !== 'string' || id.length === 0) {
    return fail('id must be a non-empty string');
  }
  if (typeof title !== 'string' || title.length === 0) {
    return fail('title must be a non-empty string');
  }
  if (bpm !== undefined && (!isFiniteNumber(bpm) || bpm <= 0)) {
    return fail('bpm must be a number > 0 when present');
  }
  if (!Array.isArray(notes) || notes.length === 0) {
    return fail('notes must be a non-empty array');
  }

  const parsed = notes.map(parseNote).sort(byTimeline);
  const deduped = dedupe(parsed);

  const melody: TargetMelody = { id, title, notes: deduped };
  if (bpm !== undefined) melody.bpm = bpm;
  return melody;
};
